import { type BrowserCookieAttributes, CookieHelper } from './index';

// Legacy UK's /bet-basket page renders from this cookie, not from the betslip, and its other writers
// (legacy grids, oc-uk, the bet-basket page itself) expect this exact shape: `betId~marketId-1` pairs
// joined by `~`. The scope must be resolveLegacyUkCookieScope's (loadChrome's betBasketCookieScope), or
// this plants a second `basket` cookie beside legacy's instead of overwriting it.
const BET_BASKET_COOKIE = 'basket';
const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

export interface BetBasketCookieScope {
  domain?: string;
  secure: boolean;
}

const toAttributes = ({ domain, secure }: BetBasketCookieScope): BrowserCookieAttributes => ({
  domain,
  sameSite: 'Lax',
  secure,
});

// Not CookieHelper.getCookie: it matches names by substring, and legacy writers outside this repo own
// the value, so a malformed escape falls back to the raw text rather than throwing into the bridge.
const readEntries = (): Map<string, string> => {
  const prefix = `${BET_BASKET_COOKIE}=`;
  const raw =
    document.cookie
      .split('; ')
      .find(cookie => cookie.startsWith(prefix))
      ?.slice(prefix.length) ?? '';
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // keep the raw value
  }
  // Legacy's own rewrite leaves a trailing `~`, hence the empty-segment filter.
  const parts = decoded.split('~').filter(Boolean);
  const entries = new Map<string, string>();
  for (let i = 0; i + 1 < parts.length; i += 2) {
    entries.set(parts[i], parts[i + 1]);
  }
  return entries;
};

const writeEntries = (entries: Map<string, string>, scope: BetBasketCookieScope): void => {
  // Created per call, not at module scope: index.ts re-exports this file, so CookieHelper is not yet
  // initialised when this module first evaluates.
  const cookieHelper = new CookieHelper();
  if (entries.size === 0) {
    cookieHelper.removeBrowserCookie(BET_BASKET_COOKIE, toAttributes(scope));
    return;
  }
  const value = [...entries].map(([betId, market]) => `${betId}~${market}`).join('~');
  cookieHelper.setBrowserCookie(BET_BASKET_COOKIE, value, Date.now() + TWO_WEEKS_MS, toAttributes(scope));
};

export const getBetBasketMarketId = (betId: number): number | undefined => {
  const marketId = Number.parseInt(readEntries().get(String(betId)) ?? '', 10);
  return Number.isNaN(marketId) ? undefined : marketId;
};

export const addToBetBasketCookie = (betId: number, marketId: number, scope: BetBasketCookieScope): void => {
  const entries = readEntries();
  entries.set(String(betId), `${marketId}-1`);
  writeEntries(entries, scope);
};

export const removeFromBetBasketCookie = (betId: number, scope: BetBasketCookieScope): void => {
  const entries = readEntries();
  if (!entries.delete(String(betId))) return;
  writeEntries(entries, scope);
};

export const clearBetBasketCookie = (scope: BetBasketCookieScope): void => {
  writeEntries(new Map(), scope);
};
